const ENGINE_ONLY_STYLE = `
<style id="iecom-html-engine-only-style">
html, body { width:100% !important; height:100% !important; margin:0 !important; overflow:hidden !important; background:transparent !important; }
#sidebar,#topbar,#bottom-dock,#viewerTools,#dock-stack,#dock-more-menu,#menu-toggle-wrap,#menu-toggle-tip,
#bomModal,#commentPanel,#panel-settings,#panel-help,#panel-section,#toast,#brand-title,#visibilityMenu,
#explode-bar,#viewcube,#op-panel,#op-toggle,#fileLoad,#loader,#edge-confirm,#edge-progress,#clipControls,
#partColor,#ColorTo,#xmlanchor,#progressPercentage,#HideOrShow,#Isolate,#ShowAll,#TransparentOrSolid,#ZoomTo,
#overlay-tl,#overlay-bl,#overlay-br,
.panel,.modal,.toast,[role="tooltip"],.tooltip,.popover { display:none !important; visibility:hidden !important; pointer-events:none !important; }
#app,#viewer { width:100% !important; height:100% !important; min-width:0 !important; min-height:0 !important; }
#viewer { position:absolute !important; inset:0 !important; }
#glcanvas,#canvas_picker,#svgWrapper { position:absolute !important; inset:0 !important; width:100% !important; height:100% !important; }
</style>`;

function escapeAttribute(value) {
    return String(value).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}

function removeLegacyOrderController(html) {
    return html.replace(/<script\b([^>]*)>([\s\S]*?)<\/script\s*>/gi, (script, attrs, body) => {
        if (/\bsrc\s*=/.test(attrs)) return script;
        const marked = /\bid\s*=\s*["']iecom-order-parts-script["']/i.test(attrs);
        const controller = body.includes('function readDataValue(id)') && body.includes('op-panel');
        if (!marked && !controller) return script;
        const strictStart = body.search(/\(function\s*\(\)\s*\{\s*["']use strict["'];/);
        if (strictStart >= 0) return `<script${attrs}>${body.slice(0, strictStart)}<\/script>`;
        if (!controller) return script;
        // Older inline copies use the same two-IIFE structure but predate the marker.
        const controllerStart = body.lastIndexOf('(function', body.indexOf('function readDataValue(id)'));
        if (controllerStart < 0) return script;
        return `<script${attrs}>${body.slice(0, controllerStart)}<\/script>`;
    });
}

export function prepareHtmlModelDocument(html, modelUrl) {
    const baseUrl = new URL('.', new URL(modelUrl, window.location.href)).href;
    const bootstrap = `<base href="${escapeAttribute(baseUrl)}"><script>window.__IECOM_HTML_ENGINE_ONLY__=true;<\/script>${ENGINE_ONLY_STYLE}`;
    let prepared = removeLegacyOrderController(String(html || ''));
    if (/<head\b[^>]*>/i.test(prepared)) {
        prepared = prepared.replace(/<head\b[^>]*>/i, (head) => `${head}${bootstrap}`);
    } else {
        prepared = `${bootstrap}${prepared}`;
    }
    return prepared;
}

export async function createHtmlModelBlob(response, modelUrl, onDownloadProgress) {
    if (!response.body) throw new Error('The HTML model response cannot be streamed.');
    const baseUrl = new URL('.', new URL(modelUrl, window.location.href)).href;
    const bootstrap = new TextEncoder().encode(
        `<base href="${escapeAttribute(baseUrl)}"><script>window.__IECOM_HTML_ENGINE_ONLY__=true;<\/script>${ENGINE_ONLY_STYLE}`
    );
    const reader = response.body.getReader();
    const chunks = [];
    let pending = new Uint8Array(0);
    let injected = false;
    const contentLength = response.headers.get('content-length');
    const totalBytes = contentLength && !response.headers.get('content-encoding') ? Number(contentLength) : 0;
    let downloadedBytes = 0;
    let lastReportedBytes = 0;
    const findHead = (bytes) => {
        for (let index = 0; index <= bytes.length - 5; index += 1) {
            if (bytes[index] !== 60 || (bytes[index + 1] | 32) !== 104 || (bytes[index + 2] | 32) !== 101 || (bytes[index + 3] | 32) !== 97 || (bytes[index + 4] | 32) !== 100) continue;
            if (bytes[index + 5] !== 62 && bytes[index + 5] > 32) continue;
            let end = index + 5;
            while (end < bytes.length && bytes[end] !== 62) end += 1;
            return end < bytes.length ? { index, end: end + 1 } : { index, end: -1 };
        }
        return null;
    };

    try {
        for (;;) {
            const { done, value } = await reader.read();
            if (done) break;
            downloadedBytes += value.byteLength;
            if (totalBytes > 0 && downloadedBytes - lastReportedBytes >= 131072) {
                lastReportedBytes = downloadedBytes;
                onDownloadProgress?.(Math.min(20, (downloadedBytes / totalBytes) * 20));
            }
            if (injected) {
                chunks.push(value);
                continue;
            }
            const combined = new Uint8Array(pending.length + value.length);
            combined.set(pending);
            combined.set(value, pending.length);
            const head = findHead(combined);
            if (head?.end >= 0) {
                chunks.push(combined.slice(0, head.end), bootstrap, combined.slice(head.end));
                pending = new Uint8Array(0);
                injected = true;
            } else if (head) {
                chunks.push(combined.slice(0, head.index));
                pending = combined.slice(head.index);
            } else if (combined.length > 16) {
                chunks.push(combined.slice(0, combined.length - 16));
                pending = combined.slice(combined.length - 16);
            } else {
                pending = combined;
            }
        }
    } catch (error) {
        reader.cancel().catch(() => {});
        throw error;
    }
    if (pending.length) chunks.push(pending);
    if (!injected) chunks.unshift(bootstrap);
    if (totalBytes > 0) onDownloadProgress?.(20);
    return new Blob(chunks, { type: 'text/html; charset=utf-8' });
}

function dispatchInput(frameWindow, element) {
    if (!element) return;
    element.dispatchEvent(new frameWindow.Event('input', { bubbles: true }));
}

function applyExplode(viewer, frameWindow, factor) {
    if (!Number.isFinite(Number(factor))) return;
    const value = Math.max(0, Math.min(1, Number(factor)));
    const doc = frameWindow.document;
    const toggle = doc.getElementById('btn-explode-toggle');
    const slider = doc.getElementById('opt-explode') || doc.getElementById('dock-explode');
    if (slider) {
        slider.value = String(value);
        dispatchInput(frameWindow, slider);
    }
    const shouldEnable = value > 0;
    if (viewer.state && typeof viewer.state.explodeEnabled === 'boolean' && viewer.state.explodeEnabled !== shouldEnable) {
        toggle?.click();
    }
}

function applyLightIntensity(viewer, intensity) {
    const multiplier = Number(intensity);
    if (!Number.isFinite(multiplier) || !viewer.scene?.traverse) return;
    viewer.scene.traverse((object) => {
        if (!object.isLight || !Number.isFinite(object.intensity)) return;
        object.userData = object.userData || {};
        if (!Number.isFinite(object.userData.__iecomBaseLightIntensity)) {
            object.userData.__iecomBaseLightIntensity = object.intensity;
        }
        object.intensity = object.userData.__iecomBaseLightIntensity * multiplier;
    });
    viewer.requestRender?.();
}

export function applyHtmlModelSettings(viewer, frameWindow, settings = {}) {
    if (!viewer || !frameWindow?.document) return;
    if (settings.highlightColor || Number.isFinite(Number(settings.highlightOpacity))) {
        viewer.setHighlightColor?.(settings.highlightColor, Number.isFinite(Number(settings.highlightOpacity)) ? Number(settings.highlightOpacity) : undefined);
    }

    const desiredNavMode = settings.navMode === 'pan' ? 'pan' : settings.navMode === 'rotate' ? 'rotate' : null;
    if (desiredNavMode && viewer.state?.navMode !== desiredNavMode) {
        frameWindow.document.getElementById('btn-nav-mode')?.click();
    }

    if (settings.explodeFactor !== undefined) applyExplode(viewer, frameWindow, settings.explodeFactor);
    if (settings.lightSettings?.intensity !== undefined) applyLightIntensity(viewer, settings.lightSettings.intensity);

    if (settings.modelOrientation) {
        frameWindow.postMessage({
            source: 'iecom-host',
            stage: 'html-orientation',
            orientation: {
                ...settings.modelOrientation,
                cameraCorner: settings.defaultCameraCorner || settings.modelOrientation.cameraCorner || 'isometric',
            },
        }, '*');
    }
}
